/**
 * Album Info editor persistence — user edits win over reload/rescan payloads.
 * Connector get_album returns album artist as artist_name (album_artist_id join).
 * The inspector form fields album_artist separately; both must stay in sync.
 */

export type AlbumMetadataFields = Record<string, unknown>;

export function normalizeAlbumArtistFields<T extends AlbumMetadataFields>(album: T | null | undefined): T {
  const row = { ...(album || {}) } as T;
  const artistName = String(row.artist_name ?? row.artist ?? '').trim();
  const albumArtist = String(row.album_artist ?? '').trim();
  const resolved = albumArtist || artistName;
  if (resolved) {
    (row as AlbumMetadataFields).album_artist = resolved;
    (row as AlbumMetadataFields).artist_name = String(row.artist_name ?? resolved);
  }
  return row;
}

/** Merge saved / pending edits into the displayed album so Save cannot revert the form. */
export function commitAlbumEditsToAlbumData(
  albumData: AlbumMetadataFields | null,
  pendingEdits: AlbumMetadataFields,
): AlbumMetadataFields {
  const merged = normalizeAlbumArtistFields({
    ...(albumData || {}),
    ...pendingEdits,
  });
  const artistEdit = String(pendingEdits.album_artist ?? pendingEdits.artist_name ?? '').trim();
  if (artistEdit) {
    merged.album_artist = artistEdit;
    if (pendingEdits.album_artist != null && pendingEdits.artist_name == null) {
      merged.artist_name = artistEdit;
    }
  }
  return merged;
}

/**
 * After save or rescan, prefer the connector re-read, then committed form state,
 * then the stale parent album prop. Never drop a user-saved album_artist.
 */
export function mergeAlbumMetadataAfterReload(args: {
  parentAlbum?: AlbumMetadataFields | null;
  committedAlbum?: AlbumMetadataFields | null;
  reloadedAlbum?: AlbumMetadataFields | null;
}): AlbumMetadataFields {
  const parent = normalizeAlbumArtistFields(args.parentAlbum);
  const committed = normalizeAlbumArtistFields(args.committedAlbum);
  const reloaded = normalizeAlbumArtistFields(args.reloadedAlbum);
  const merged = {
    ...parent,
    ...committed,
    ...reloaded,
  };
  const savedArtist = String(
    committed.album_artist
    || committed.artist_name
    || reloaded.album_artist
    || reloaded.artist_name
    || parent.album_artist
    || parent.artist_name
    || '',
  ).trim();
  if (savedArtist) {
    merged.album_artist = savedArtist;
    if (!String(merged.artist_name || '').trim()) merged.artist_name = savedArtist;
  }
  return merged;
}

export function localAlbumWriteWasSkipped(result: {
  fields_updated?: unknown;
  fieldsUpdated?: unknown;
  new_catalog_version?: unknown;
  newCatalogVersion?: unknown;
} | null | undefined, sentFieldCount: number): boolean {
  if (sentFieldCount <= 0) return false;
  const updated = result?.fields_updated ?? result?.fieldsUpdated;
  const written = Array.isArray(updated) ? updated.length : 0;
  const version = Number(result?.new_catalog_version ?? result?.newCatalogVersion ?? 0);
  return written === 0 && version === 0;
}
